import { useEffect, useMemo, useState } from "react";
import { RECORD_TYPES, type RecordType } from "../lib/recordTypes";

type MigrationKind = "website" | "email" | "dns-provider" | "cdn";

type Step = {
  id: string;
  phase: "Before" | "Change" | "After";
  title: string;
  detail: string;
  href?: (input: ChecklistInput) => string;
  cta?: string;
};

type ChecklistInput = {
  host: string;
  type: RecordType;
  oldValue: string;
  newValue: string;
  kind: MigrationKind;
};

const KIND_LABEL: Record<MigrationKind, string> = {
  website: "Website move",
  email: "Email move",
  "dns-provider": "DNS provider move",
  cdn: "CDN launch",
};

const KIND_DEFAULT_TYPE: Record<MigrationKind, RecordType> = {
  website: "A",
  email: "MX",
  "dns-provider": "NS",
  cdn: "CNAME",
};

const BASE_STEPS: Step[] = [
  {
    id: "inventory",
    phase: "Before",
    title: "Inventory the current DNS",
    detail: "Capture what is live before you edit anything, including web, mail, TXT verification, and nameserver records.",
    href: ({ host }) => `/dns-lookup?host=${encodeURIComponent(host)}`,
    cta: "Open all-record lookup",
  },
  {
    id: "health",
    phase: "Before",
    title: "Run a DNS health check",
    detail: "Catch missing MX, SPF, SOA, delegation, or CAA issues before a migration makes them harder to explain.",
    href: ({ host }) => `/dns-health?domain=${encodeURIComponent(host)}`,
    cta: "Run health check",
  },
  {
    id: "ttl",
    phase: "Before",
    title: "Lower TTL before the cutover",
    detail: "If the old record has a long TTL, lower it one old-TTL window before the change. Lowering TTL at cutover time is usually too late.",
    href: () => "/glossary/ttl",
    cta: "Read TTL guide",
  },
  {
    id: "delegation",
    phase: "Before",
    title: "Confirm the authoritative provider",
    detail: "Make sure the registrar, zone NS records, and provider dashboard all point to the same DNS authority.",
    href: ({ host }) => `/delegation-check?domain=${encodeURIComponent(rootDomain(host))}`,
    cta: "Check delegation",
  },
  {
    id: "publish",
    phase: "Change",
    title: "Publish the new record",
    detail: "Change the record at the authoritative provider, then leave the old dashboard untouched until you confirm traffic is stable.",
  },
  {
    id: "compare",
    phase: "Change",
    title: "Compare old vs new answers",
    detail: "Use the tracker to see which resolvers still return the old value, which have switched, and whether anything unexpected appeared.",
    href: ({ host, type, oldValue, newValue }) => trackerUrl(host, type, oldValue, newValue),
    cta: "Open tracker",
  },
  {
    id: "alert",
    phase: "After",
    title: "Set a one-shot propagation alert",
    detail: "Let WhereIsDNS email you when all resolvers return the new value, or if an unexpected answer shows up.",
    href: ({ host, type, oldValue, newValue }) => trackerUrl(host, type, oldValue, newValue),
    cta: "Create alert",
  },
  {
    id: "verify",
    phase: "After",
    title: "Verify the final public answer",
    detail: "Run a normal resolver check after the tracker looks clean, then share the report if someone else needs proof.",
    href: ({ host, type }) => `/?host=${encodeURIComponent(host)}&type=${type}`,
    cta: "Check propagation",
  },
  {
    id: "restore",
    phase: "After",
    title: "Restore steady-state TTL",
    detail: "After traffic, mail, or CDN behavior is stable, raise TTL back to the value you want for normal operation.",
  },
];

const KIND_HINT: Record<MigrationKind, string> = {
  website: "Watch A and AAAA records, and remember that a stale AAAA can affect IPv6 users even when IPv4 looks fixed.",
  email: "After MX moves, check SPF, DKIM, and DMARC too. Inbound routing and outbound authentication fail in different ways.",
  "dns-provider": "Nameserver changes can take longer than record changes because the parent zone and recursive caches both matter.",
  cdn: "For proxied CDN records, public DNS may return edge hostnames or IPs rather than your origin. Compare against the public target you expect.",
};

export function DnsMigrationChecklist() {
  const initial = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const kind = normalizeKind(p.get("kind") ?? "website");
    return {
      host: normalizeHost(p.get("host") ?? "epochcalc.com"),
      type: normalizeType(p.get("type") ?? KIND_DEFAULT_TYPE[kind]),
      oldValue: p.get("old") ?? "",
      newValue: p.get("new") ?? "",
      kind,
    };
  }, []);
  const [input, setInput] = useState<ChecklistInput>(initial);
  const [done, setDone] = useState<Record<string, boolean>>(() => readDone(initial.host, initial.kind));
  const complete = BASE_STEPS.filter((step) => done[step.id]).length;

  useEffect(() => {
    setDone(readDone(input.host, input.kind));
  }, [input.host, input.kind]);

  useEffect(() => {
    localStorage.setItem(storageKey(input.host, input.kind), JSON.stringify(done));
  }, [done, input.host, input.kind]);

  const setKind = (kind: MigrationKind) => {
    setInput((current) => ({ ...current, kind, type: KIND_DEFAULT_TYPE[kind] }));
  };

  return (
    <main className="console migration-page">
      <section className="migration-hero">
        <div>
          <span className="landing-eyebrow">DNS migration checklist</span>
          <h1>Plan the change before DNS starts lying to you.</h1>
          <p className="landing-lede">
            A practical checklist for moving websites, mail, nameservers, or CDN records without losing track of TTL, delegation, old answers, and final verification.
          </p>
        </div>
        <div className="migration-score" aria-label={`${complete} of ${BASE_STEPS.length} checklist steps complete`}>
          <b>{complete}/{BASE_STEPS.length}</b>
          <span>complete</span>
        </div>
      </section>

      <section className="migration-setup" aria-label="Migration setup">
        <label className="watch-field">
          <span className="gen-label">Domain or hostname</span>
          <input value={input.host} onChange={(e) => setInput({ ...input, host: normalizeHost(e.target.value) })} placeholder="example.com" spellCheck={false} autoCapitalize="off" autoCorrect="off" />
        </label>
        <label className="watch-field">
          <span className="gen-label">Migration type</span>
          <select value={input.kind} onChange={(e) => setKind(e.target.value as MigrationKind)}>
            {Object.entries(KIND_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="watch-field">
          <span className="gen-label">Record type</span>
          <select value={input.type} onChange={(e) => setInput({ ...input, type: e.target.value as RecordType })}>
            {RECORD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </label>
        <label className="watch-field">
          <span className="gen-label">Old value</span>
          <textarea value={input.oldValue} onChange={(e) => setInput({ ...input, oldValue: e.target.value })} placeholder="Old IP, MX host, CNAME, TXT value..." rows={2} />
        </label>
        <label className="watch-field">
          <span className="gen-label">New value</span>
          <textarea value={input.newValue} onChange={(e) => setInput({ ...input, newValue: e.target.value })} placeholder="New IP, MX host, CNAME, TXT value..." rows={2} />
        </label>
      </section>

      <section className="migration-tip">
        <span className="answer-eyebrow">{KIND_LABEL[input.kind]}</span>
        <p>{KIND_HINT[input.kind]}</p>
      </section>

      <section className="migration-steps" aria-label="DNS migration checklist steps">
        {BASE_STEPS.map((step, index) => {
          const checked = !!done[step.id];
          const href = step.href?.(input);
          return (
            <article className={`migration-step ${checked ? "migration-step-done" : ""}`} key={step.id}>
              <label className="migration-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setDone((current) => ({ ...current, [step.id]: e.target.checked }))}
                />
                <span>{String(index + 1).padStart(2, "0")}</span>
              </label>
              <div>
                <span className="migration-phase">{step.phase}</span>
                <h2>{step.title}</h2>
                <p>{step.detail}</p>
              </div>
              {href && input.host ? <a className="migration-action" href={href}>{step.cta}</a> : null}
            </article>
          );
        })}
      </section>
    </main>
  );
}

function trackerUrl(host: string, type: RecordType, oldValue: string, newValue: string): string {
  const params = new URLSearchParams({ host, type });
  if (oldValue.trim()) params.set("old", oldValue.trim());
  if (newValue.trim()) params.set("new", newValue.trim());
  return `/dns-change-tracker?${params.toString()}`;
}

function storageKey(host: string, kind: MigrationKind): string {
  return `whereisdns:migration:${kind}:${host || "draft"}`;
}

function readDone(host: string, kind: MigrationKind): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(storageKey(host, kind)) || "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

function normalizeKind(value: string): MigrationKind {
  return value === "email" || value === "dns-provider" || value === "cdn" ? value : "website";
}

function normalizeType(value: string): RecordType {
  const up = value.toUpperCase();
  return (RECORD_TYPES as readonly string[]).includes(up) ? up as RecordType : "A";
}

function normalizeHost(raw: string): string {
  return raw.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}

function rootDomain(host: string): string {
  const parts = normalizeHost(host).split(".").filter(Boolean);
  return parts.length > 2 ? parts.slice(-2).join(".") : parts.join(".");
}
